import React from 'react';
import { Circle, G, Line, Path, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { DT } from '../tokens';

/** Q_L at 50 Pa — envelope with distributed leak paths & magnified crack callout. */
export function DiagramBETAirLeakageRate() {
  const hx = 80, hy = 70, hw = 200, hh = 150;

  return (
    <DiagramCanvas>
      {/* Envelope */}
      <Rect x={hx} y={hy} width={hw} height={hh} fill={DT.fill} stroke={DT.stroke} strokeWidth="2" />
      {/* Roof line */}
      <Polygon points={`${hx - 6},${hy} ${hx + hw / 2},${hy - 28} ${hx + hw + 6},${hy}`}
        fill={DT.fill} stroke={DT.stroke} strokeWidth="1.6" />

      {/* Structural hatching */}
      {[0, 1, 2, 3, 4].map(i => (
        <Line key={i} x1={hx} y1={hy + 20 + i * 26} x2={hx + hw} y2={hy + 20 + i * 26}
          stroke={DT.stroke} strokeWidth="0.3" opacity={0.3} />
      ))}

      {/* Leak paths (cracks) with outflow arrows */}
      {[
        [hx, hy + 30, -1, 0], [hx, hy + 80, -1, 0], [hx, hy + 130, -1, 0],
        [hx + hw, hy + 45, 1, 0], [hx + hw, hy + 100, 1, 0],
        [hx + 50, hy, 0, -1], [hx + 130, hy, 0, -1], [hx + 180, hy, 0, -1],
        [hx + 40, hy + hh, 0, 1], [hx + 150, hy + hh, 0, 1],
      ].map(([lx, ly, dx, dy], i) => (
        <G key={i}>
          {/* Jagged crack */}
          <Path d={`M${lx - dx * 2},${ly - dy * 2} l${-dx * 6 + dy * 2},${-dy * 6 + dx * 2} l${dx * 2},${dy * 2} l${-dx * 6},${-dy * 6}`}
            fill="none" stroke={DT.varColor} strokeWidth="1.3" />
          {/* Arrow out */}
          <Line x1={lx} y1={ly} x2={lx + dx * 14} y2={ly + dy * 14} stroke={DT.varColor} strokeWidth="1.2" strokeDasharray="3,2" />
          <Polygon points={`${lx + dx * 14},${ly + dy * 14} ${lx + dx * 8 - dy * 3},${ly + dy * 8 - dx * 3} ${lx + dx * 8 + dy * 3},${ly + dy * 8 + dx * 3}`}
            fill={DT.varColor} opacity={0.8} />
        </G>
      ))}

      {/* Magnified crack callout (top right) */}
      <Circle cx={295} cy={50} r={22} fill="rgba(0,0,0,0.5)" stroke={DT.varColor} strokeWidth="1.4" />
      <Path d="M285,40 L292,48 L288,52 L298,58 L295,62" fill="none" stroke={DT.varColor} strokeWidth="1.4" />
      <SvgText x={295} y={85} fill={DT.varColor} fontSize="7" fontFamily={DT.mono} textAnchor="middle">crack detail</SvgText>
      <Line x1={275} y1={50} x2={hx} y2={hy + 30} stroke={DT.varColor} strokeWidth="0.5" strokeDasharray="2,2" />

      {/* Pressure indicator inside */}
      <Rect x={hx + hw / 2 - 30} y={hy + hh / 2 - 10} width={60} height={22} fill="rgba(0,0,0,0.6)" stroke={DT.varColor} strokeWidth="1" />
      <SvgText x={hx + hw / 2} y={hy + hh / 2 + 5} fill={DT.varColor} fontSize="10" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">ΔP = 50 Pa</SvgText>

      {/* Q_L gauge (bottom) */}
      <Rect x={30} y={225} width={130} height={20} fill="rgba(0,0,0,0.5)" stroke={DT.varColor} strokeWidth="0.8" />
      <SvgText x={95} y={238} fill={DT.varColor} fontSize="9" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">Q_L = Σ Q_i (CFM50)</SvgText>

      {/* Labels */}
      <LeaderLine x1={hx} y1={hy + 30} x2={22} y2={60} varName="Q_i" desc="per-leak flow" anchor="start" />
      <LeaderLine x1={hx + hw / 2} y1={hy - 20} x2={270} y2={230} varName="envelope" desc="6-sided boundary" anchor="end" />
      <LeaderLine x1={hx + hw + 14} y1={hy + 45} x2={335} y2={150} varName="Q_L" desc="total leakage" anchor="end" />

      <FormulaTag formula="Q_L,50 = C · (50)^n    (CFM @ 50 Pa)" />
    </DiagramCanvas>
  );
}
